import React, { useState, Suspense } from "react";
import { Routes, Route, Link } from "react-router-dom";
import Navigation from "./components/Navigation";
import ScrollToTop from "./components/ScrollToTop";
import PageLoader from "./components/PageLoader";
import VoiceNavigation from "./components/VoiceNavigation";
import AccessibilityToggle from "./components/AccessibilityToggle";
import VersionInfo from "./components/VersionInfo";
import { Clock, Phone, Mail, FileText, MapPin } from "lucide-react";

const HomePage = React.lazy(() => import("./pages/Home"));
const AboutPage = React.lazy(() => import("./pages/About"));
const ServicesPage = React.lazy(() => import("./pages/Services"));
const LocationPage = React.lazy(() => import("./pages/Location"));
const Contact = React.lazy(() => import("./pages/Contact"));
const Provider = React.lazy(() => import("./pages/Provider"));
const Careers = React.lazy(() => import("./pages/Careers"));
const DirectAccessPage = React.lazy(() => import("./pages/DirectAccess"));
const NotFoundPage = React.lazy(() => import("./pages/NotFound"));
const TermsAndConditions = React.lazy(() => import("./pages/TermsAndConditions"));
const PrivacyPolicy = React.lazy(() => import("./pages/PrivacyPolicy"));
const FAQ = React.lazy(() => import("./pages/FAQ"));
const ChronicPain = React.lazy(() => import("./pages/services/ChronicPain"));
const SportsInjuries = React.lazy(() => import("./pages/services/SportsInjuries"));
const PostSurgical = React.lazy(() => import("./pages/services/PostSurgical"));
const HeadachesTMJ = React.lazy(() => import("./pages/services/HeadachesTMJ"));
const VestibularBalance = React.lazy(() => import("./pages/services/VestibularBalance"));
const WorkInjuries = React.lazy(() => import("./pages/services/WorkInjuries"));
const MovementImpairment = React.lazy(() => import("./pages/services/MovementImpairment"));
const NeuromuscularDisorders = React.lazy(() => import("./pages/services/NeuromuscularDisorders"));
const PediatricTherapy = React.lazy(() => import("./pages/services/PediatricTherapy"));
const AutoAccidentInjuries = React.lazy(() => import("./pages/services/AutoAccidentInjuries"));
const ResourceCenter = React.lazy(() => import("./pages/ResourceCenter"));
const Technology = React.lazy(() => import("./pages/Technology"));
const MotiPhysio = React.lazy(() => import("./pages/technology/MotiPhysio"));
const Baiobit = React.lazy(() => import("./pages/technology/Baiobit"));
const VestibularFirstGoggles = React.lazy(() => import("./pages/technology/VestibularFirstGoggles"));
const Blog = React.lazy(() => import("./pages/Blog"));
const BlogPost = React.lazy(() => import("./pages/BlogPost"));
const CityLanding = React.lazy(() => import("./pages/CityLanding"));

const HolisticTherapyWebsite: React.FC = () => {
  const [isVoiceNavigationEnabled, setIsVoiceNavigationEnabled] = useState(false);

  const toggleVoiceNavigation = () => {
    setIsVoiceNavigationEnabled(!isVoiceNavigationEnabled);
  };

  return (
    // <Router>
      <div className="min-h-screen bg-white">
        {/* Top Contact Bar */}
        <div className="bg-slate-50 border-b border-slate-200">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-2 sm:py-3">
            {/* Desktop Layout */}
            <div className="hidden md:flex justify-between items-center">
              <div className="flex items-center space-x-6 text-sm">
                <div className="flex items-center text-slate-900">
                  <Clock className="w-4 h-4 mr-2" />
                  <span className="font-medium">
                    Mon-Fri: 7AM-7PM
                  </span>
                </div>
              </div>
              <div className="flex items-center space-x-6 text-sm">
                <div className="flex items-center text-slate-900">
                  <FileText className="w-4 h-4 mr-2" />
                  <span className="font-medium">(517) 855-2990</span>
                </div>
                <div className="flex items-center text-slate-900">
                  <Phone className="w-4 h-4 mr-2" />
                  <span className="font-medium">(517) 908-0800</span>
                </div>
                <div className="flex items-center text-slate-900">
                  <Mail className="w-4 h-4 mr-2" />
                  <span className="font-medium">info@holisticmanualpt.com</span>
                </div>
              </div>
            </div>

            {/* Tablet Layout */}
            <div className="hidden sm:flex md:hidden flex-col space-y-2">
              <div className="flex items-center justify-center text-sm">
                <div className="flex items-center text-slate-900">
                  <Clock className="w-4 h-4 mr-2" />
                  <span className="font-medium">
                    Mon-Fri: 7AM-7PM
                  </span>
                </div>
              </div>
              <div className="flex items-center justify-center space-x-4 text-sm">
                <div className="flex items-center text-slate-900">
                  <Phone className="w-4 h-4 mr-2" />
                  <span className="font-medium">(517) 908-0800</span>
                </div>
                <div className="flex items-center text-slate-900">
                  <Mail className="w-4 h-4 mr-1" />
                  <span className="font-medium">info@holisticmanualpt.com</span>
                </div>
              </div>
            </div>

            {/* Mobile Layout */}
            <div className="flex sm:hidden flex-col space-y-2 text-center">
              <div className="flex items-center justify-center text-xs">
                <div className="flex items-center text-slate-900">
                  <Clock className="w-3 h-3 mr-1" />
                  <span className="font-medium">Mon-Fri: 7AM-7PM</span>
                </div>
              </div>
              <div className="flex flex-col space-y-1 text-xs">
                <div className="flex items-center justify-center text-slate-900">
                  <Phone className="w-3 h-3 mr-1" />
                  <span className="font-medium">(517) 908-0800</span>
                </div>
                <div className="flex items-center justify-center text-slate-900">
                  <Mail className="w-3 h-3 mr-1" />
                  <span className="font-medium">info@holisticmanualpt.com</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <Navigation />
        <ScrollToTop />

        {/* Main Content */}
        <Suspense fallback={<PageLoader />}>
          <Routes>
            <Route path="/" element={<HomePage />} />
            <Route path="/about" element={<AboutPage />} />
            <Route path="/services" element={<ServicesPage />} />
            <Route path="/services/:serviceId" element={<ServicesPage />} />
            <Route path="/resource-center" element={<ResourceCenter />} />
            <Route path="/location" element={<LocationPage />} />
            <Route path="/direct-access" element={<DirectAccessPage />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/provider" element={<Provider />} />
            <Route path="/careers" element={<Careers />} />
            <Route path="/terms-and-conditions" element={<TermsAndConditions />} />
            <Route path="/privacy-policy" element={<PrivacyPolicy />} />
            <Route path="/faq" element={<FAQ />} />
            <Route path="/services/chronic-pain" element={<ChronicPain />} />
            <Route path="/services/sports-injuries" element={<SportsInjuries />} />
            <Route path="/services/post-surgical" element={<PostSurgical />} />
            <Route path="/services/headaches-tmj" element={<HeadachesTMJ />} />
            <Route path="/services/vestibular-balance" element={<VestibularBalance />} />
            <Route path="/services/work-injuries" element={<WorkInjuries />} />
            <Route path="/services/movement-impairment" element={<MovementImpairment />} />
            <Route path="/services/neuromuscular-disorders" element={<NeuromuscularDisorders />} />
            <Route path="/services/pediatric-therapy" element={<PediatricTherapy />} />
            <Route path="/services/auto-accident-injuries" element={<AutoAccidentInjuries />} />
            <Route path="/technology" element={<Technology />} />
            <Route path="/technology/moti-physio" element={<MotiPhysio />} />
            <Route path="/technology/baiobit" element={<Baiobit />} />
            <Route path="/technology/vestibular-goggles" element={<VestibularFirstGoggles />} />
            <Route path="/blog" element={<Blog />} />
            <Route path="/blog/:slug" element={<BlogPost />} />
            <Route path="/physical-therapy-:city" element={<CityLanding />} />
            <Route path="*" element={<NotFoundPage />} />
          </Routes>
        </Suspense>
        {/* Footer */}
        <footer className="bg-slate-900 text-white py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-8 lg:gap-6">
              {/* Company Info */}
              <div>
                <div className="flex items-center mb-6">
                  <div className="w-10 h-10 mr-3">
                    <img
                      src="/HMPT Logo.png"
                      alt="Holistic Manual Physical Therapy"
                      className="w-10 h-10 object-contain"
                    />
                  </div>
                  <div>
                    <div className="text-lg font-bold">Holistic Manual</div>
                    <div className="text-sm text-slate-400">
                      Physical Therapy
                    </div>
                  </div>
                </div>
                <p className="text-sm font-bold leading-relaxed">
                  <span className="block">Restoring Function.</span>
                  <span className="block">Reclaiming Life.</span>
                </p>
                <div className="flex space-x-4">
                  <a href="https://www.facebook.com/holisticmanualpt" target="_blank" rel="noopener noreferrer" aria-label="Follow us on Facebook" className="w-8 h-8 bg-slate-900 rounded-full flex items-center justify-center hover:bg-slate-800 transition-colors cursor-pointer">
                    <span className="text-xs font-bold" aria-hidden="true">f</span>
                  </a>
                  <a href="https://www.instagram.com/holistic_manual_pt" target="_blank" rel="noopener noreferrer" aria-label="Follow us on Instagram" className="w-8 h-8 bg-slate-900 rounded-full flex items-center justify-center hover:bg-slate-800 transition-colors cursor-pointer">
                    <span className="text-xs font-bold" aria-hidden="true">ig</span>
                  </a>
                  <a href="https://www.linkedin.com/company/holistic-manual-physical-therapy-llc/" target="_blank" rel="noopener noreferrer" aria-label="Follow us on LinkedIn" className="w-8 h-8 bg-slate-900 rounded-full flex items-center justify-center hover:bg-slate-800 transition-colors cursor-pointer">
                    <span className="text-xs font-bold" aria-hidden="true">in</span>
                  </a>
                </div>
              </div>

              {/* Quick Links */}
              <div>
                <h4 className="font-bold text-lg mb-6">Quick Links</h4>
                <ul className="space-y-3">
                  <li>
                    <Link to="/services" className="text-slate-400 hover:text-white transition-colors">
                      Specialty Services
                    </Link>
                  </li>
                  <li>
                    <Link to="/technology" className="text-slate-400 hover:text-white transition-colors">
                      Advanced Technology
                    </Link>
                  </li>
                  <li>
                    <Link to="/resource-center" className="text-slate-400 hover:text-white transition-colors">
                      Resource Center
                    </Link>
                  </li>
                  <li>
                    <Link to="/location" className="text-slate-400 hover:text-white transition-colors">
                      Location
                    </Link>
                  </li>
                  <li>
                    <Link to="/direct-access" className="text-slate-400 hover:text-white transition-colors">
                      Direct Access
                    </Link>
                  </li>
                  <li>
                    <Link to="/provider" className="text-slate-400 hover:text-white transition-colors">
                      Provider
                    </Link>
                  </li>
                  <li>
                    <Link to="/blog" className="text-slate-400 hover:text-white transition-colors">
                      Blog
                    </Link>
                  </li>
                  <li>
                    <Link to="/careers" className="text-slate-400 hover:text-white transition-colors">
                      Careers
                    </Link>
                  </li>
                </ul>
              </div>

              {/* Legal Links */}
              <div>
                <h4 className="font-bold text-lg mb-6">Legal</h4>
                <ul className="space-y-3">
                  <li>
                    <Link to="/privacy-policy" className="text-slate-400 hover:text-white transition-colors">
                      Privacy Policy
                    </Link>
                  </li>
                  <li>
                    <Link to="/terms-and-conditions" className="text-slate-400 hover:text-white transition-colors">
                      Terms & Conditions
                    </Link>
                  </li>
                  <li>
                    <Link to="/faq" className="text-slate-400 hover:text-white transition-colors">
                      FAQ
                    </Link>
                  </li>
                </ul>
              </div>

              {/* Contact Info */}
              <div>
                <h4 className="font-bold text-lg mb-6">Contact Info</h4>
                <div className="space-y-4">
                  <div className="flex items-center">
                    <FileText className="w-4 h-4 mr-3 text-slate-400" />
                    <span className="text-slate-400">(517) 855-2990</span>
                  </div>
                  <div className="flex items-center">
                    <Phone className="w-4 h-4 mr-3 text-slate-400" />
                    <span className="text-slate-400">(517) 908-0800</span>
                  </div>
                  <div className="flex items-center">
                    <Mail className="w-4 h-4 mr-3 text-slate-400" />
                    <span className="text-slate-400">
                      info@holisticmanualpt.com
                    </span>
                  </div>
                  <div className="flex items-start">
                    <MapPin className="w-4 h-4 mr-3 mt-1 text-slate-400" />
                    <div className="text-slate-400">
                      <div>5700 Executive Dr Ste 3</div>
                      <div>Lansing, MI 48911</div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Office Hours */}
              <div>
                <h4 className="font-bold text-lg mb-6">Office Hours</h4>
                <div className="space-y-3">
                  <div className="flex justify-between items-center">
                    <span className="text-slate-400">Monday</span>
                    <span className="text-white font-medium">7AM - 7PM</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-slate-400">Tuesday</span>
                    <span className="text-white font-medium">7AM - 7PM</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-slate-400">Wednesday</span>
                    <span className="text-white font-medium">7AM - 7PM</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-slate-400">Thursday</span>
                    <span className="text-white font-medium">7AM - 7PM</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-slate-400">Friday</span>
                    <span className="text-white font-medium">7AM - 7PM</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-slate-400">Weekend</span>
                    <span className="text-white font-medium">By Request</span>
                  </div>
                  <div className="pt-4 border-t border-slate-800">
                    <div className="text-sm text-slate-500">
                      We offer flexible scheduling, including after-hours or
                      weekend appointments by request
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Premium Branding Section */}
            <div className="border-t border-slate-800 mt-12 pt-8">
              {/* Main Copyright Row */}
              <div className="flex flex-col lg:flex-row justify-between items-center space-y-6 lg:space-y-0 mb-6">
                <div className="text-center lg:text-left">
                  <p className="text-slate-400">
                    &copy; {new Date().getFullYear()} Holistic Manual Physical  Therapy. All rights reserved.
                  </p>
                </div>
                <div className="flex flex-wrap justify-center lg:justify-end gap-6 text-sm">
                  <Link to="/privacy-policy" className="text-slate-400 hover:text-white transition-colors">
                    Privacy Policy
                  </Link>
                  <Link to="/terms-and-conditions" className="text-slate-400 hover:text-white transition-colors">
                    Terms & Conditions
                  </Link>
                </div>
              </div>

              {/* Cognitive Craft Hub Branding */}
              <div className="max-w-2xl mx-auto">
                <div className="border border-slate-700 rounded-xl bg-slate-800/40 px-6 py-4">
                  <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
                    {/* Left — Branding */}
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 bg-amber-400/10 border border-amber-400/30 rounded-lg flex items-center justify-center flex-shrink-0">
                        <svg className="w-5 h-5 text-amber-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" />
                        </svg>
                      </div>
                      <div>
                        <p className="text-slate-500 text-xs uppercase tracking-widest font-medium">Designed &amp; Developed by</p>
                        <a
                          href="https://www.cognitivecrafthub.com"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-base font-bold text-white hover:text-amber-400 transition-colors duration-200"
                        >
                          Cognitive Craft Hub
                        </a>
                      </div>
                    </div>

                    {/* Right — CTA */}
                    <a
                      href="https://www.cognitivecrafthub.com"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 border border-amber-500/50 text-amber-400 hover:bg-amber-400 hover:text-slate-900 px-5 py-2.5 rounded-lg font-semibold text-sm transition-all duration-200 whitespace-nowrap"
                    >
                      Get a Website Like This
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                      </svg>
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </footer>

        {/* Accessibility Features */}
        <AccessibilityToggle
          onVoiceNavigationToggle={toggleVoiceNavigation}
          isVoiceNavigationEnabled={isVoiceNavigationEnabled}
        />
        
        <VoiceNavigation
          isEnabled={isVoiceNavigationEnabled}
          onToggle={toggleVoiceNavigation}
        />

        {/* Version Info */}
        <VersionInfo />
      </div>
    // </Router>
  );
};

export default HolisticTherapyWebsite;
